<template>
  <div class="schedule-date-selector-wrapper h-full flex-col!">
    <div class="flex w-full justify-around">
      <div class="date-selector" style="width: 220px">
        <div class="date-selector-text">Start date:</div>
        <div class="date-selector-datepicker date-selector-start" style="margin: 0">
          <RfMenu
            ref="startDateMenu"
            class="w-full [&_.rf-menu--button]:flex-1"
            variant="outlined"
            hideIcon
            :disabled="disabled || disableStartDate"
          >
            <template #activator>
              <v-text-field
                persistentPlaceholder
                :appendInnerIcon="showClearIcon ? undefined : 'md:event'"
                readonly
                :disabled="disabled || disableStartDate"
                variant="plain"
                density="compact"
                hideDetails
                class="px-4 pb-2"
                :modelValue="startDateRender"
                persistentClear
                clearIcon="mdi:mdi-close-circle"
                :clearable="showClearIcon"
                @click:clear="clearStartDate"
              ></v-text-field>
            </template>
            <template #menu>
              <v-date-picker
                v-model="startDate"
                hideHeader
                :max="endDate"
                @update:modelValue="$refs.startDateMenu?.hide()"
              ></v-date-picker>
            </template>
          </RfMenu>
        </div>
      </div>
      <div class="date-selector" style="width: 220px">
        <div class="date-selector-text">End date:</div>
        <div class="date-selector-datepicker date-selector-end" style="margin: 0">
          <RfMenu
            ref="endDateMenu"
            class="w-full [&_.rf-menu--button]:flex-1"
            variant="outlined"
            hideIcon
            :disabled="disabled || noEndDate || isPushSentOnce"
          >
            <template #activator>
              <v-text-field
                persistentPlaceholder
                :appendInnerIcon="showClearIcon ? undefined : 'md:event'"
                readonly
                variant="plain"
                :modelValue="noEndDate ? '∞' : endDateRender"
                density="compact"
                hideDetails
                clearIcon="mdi:mdi-close-circle"
                class="px-4 pb-2"
                persistentClear
                :clearable="showClearIcon"
                @click:clear="clearEndDate"
              ></v-text-field>
            </template>
            <template #menu>
              <v-date-picker
                v-model="endDate"
                hideHeader
                :disabled="disabled || isPushSentOnce"
                :min="startDate"
                @update:modelValue="$refs.endDateMenu?.hide()"
              ></v-date-picker>
            </template>
          </RfMenu>
        </div>
      </div>
    </div>
    <div v-if="!hideTime" class="mt-5 flex w-full justify-around">
      <div class="date-selector" style="width: 220px">
        <div class="date-selector-text">Start time (24hr):</div>
        <div class="date-selector-datepicker flex gap-1">
          <RfHHMMPicker
            v-if="isPushNotification"
            v-model="startTime"
            :interval="5"
            :disabled="disabled"
            class="min-w-px flex-1"
          />
          <RfTimePicker
            v-else
            :initTime="StringUtils.intervalIndexByHhmm(startTime)"
            :disabled="disabled"
            autoWidth
            class="min-w-px flex-1"
            sameWidth
            @updateTime="time => (startTime = StringUtils.hhmmBy30MinIntervalIndex(time))"
          />
        </div>
      </div>
      <div class="date-selector" style="width: 220px">
        <div class="date-selector-text">End time (24hr):</div>
        <div class="date-selector-datepicker flex gap-1">
          <RfHHMMPicker
            v-if="isPushNotification"
            v-model="endTime"
            :interval="5"
            :disabled="disabled || noEndDate || isPushSentOnce"
            class="min-w-px flex-1"
          />
          <RfTimePicker
            v-else
            :initTime="StringUtils.intervalIndexByHhmm(endTime)"
            :disabled="disabled || noEndDate || isPushSentOnce"
            autoWidth
            class="min-w-px flex-1"
            sameWidth
            @updateTime="time => (endTime = StringUtils.hhmmBy30MinIntervalIndex(time))"
          />
        </div>
      </div>
    </div>
    <div v-if="!experiments" class="mt-2 flex w-full justify-around">
      <div class="date-selector" style="width: 220px"></div>
      <div
        v-if="!isPushNotification || isEventTriggered"
        class="date-selector"
        style="width: 220px"
      >
        <RfSwitch
          v-model="noEndDate"
          title="No end date"
          class="flex-row-reverse"
          @update:modelValue="handleNoEndDate"
        />
      </div>
    </div>

    <div v-if="isPushNotification" class="mx-12 mt-5 flex flex-col self-start">
      <!-- Push V2: event-triggered not implemented -->
      <v-radio-group v-if="false" v-model="isEventTriggered" class="my-1" hideDetails inline>
        <v-radio label="Schedule triggered" :value="false" :disabled="isRunningOrEnded" />
        <v-radio label="Event triggered" :value="true" class="ml-5" :disabled="isRunningOrEnded" />
      </v-radio-group>
      <template v-if="!isEventTriggered">
        <v-select
          v-model.trim="frequency"
          class="mt-2 w-40"
          bgColor="#ffffff"
          variant="outlined"
          density="compact"
          hideDetails
          itemTitle="text"
          :items="[
            { value: '', text: 'Once' },
            { value: '1.day', text: 'Daily' },
            { value: '1.week', text: 'Weekly' },
            { value: '1.month', text: 'Monthly' },
          ]"
          label="Frequency"
        />
        <v-checkbox
          v-model="localTime"
          class="mt-2"
          label="Send at device local time"
          density="compact"
          hideDetails
          trueValue="true"
          falseValue="false"
        />
        <!-- Push V2: quiet-time not implemented -->
        <v-checkbox
          v-if="false"
          :modelValue="showQuietTime"
          class="ml-2"
          label="Quiet time"
          density="compact"
          hideDetails
          @update:modelValue="toggleQuietTime"
        />
        <div v-if="showQuietTime" class="mb-6 ml-9 flex items-center gap-4">
          <div>From:</div>
          <div class="date-selector flex w-28">
            <RfHHMMPicker v-model="quietTimeStart" :disabled="disabled" />
          </div>
          <div>to:</div>
          <div class="date-selector flex w-28">
            <RfHHMMPicker v-model="quietTimeEnd" :disabled="disabled" />
          </div>
          <div>(inclusive)</div>
        </div>
      </template>

      <div class="px-1 pt-2 pb-2">
        Push notification will
        <span v-if="isEventTriggered">
          be sent when the schedule is active, and an event is sent to the External Events API.
        </span>
        <span v-else-if="repeatTimes > 0">
          be sent at
          {{ quietTimeStart && quietTimeEnd ? cappedStartDateInTimezone : fullStartDateInTimezone }}
          <span v-if="repeatTimes > 1">
            <br />
            This repeats every {{ frequency?.replace(".", " ") }} for a total of
            {{ repeatTimes }} times
            <template v-if="quietRepeatTimes">
              (excluded {{ quietRepeatTimes }} sends during quiet time)
            </template>
            <br />
            The last notification will be sent at {{ cappedEndDateInTimezone }}
          </span>
        </span>
        <span v-else> not be sent! </span>
      </div>
      <div v-if="localTime !== 'true' && quietTimeStart && quietTimeEnd" class="px-1 pb-2">
        Note: Quiet time is in device local time. Calculations above assume same timezone
      </div>
      <div v-if="localTime === 'true'" class="px-1 pb-2">
        <v-icon color="warning" size="16">md:warning</v-icon>
        When scheduled, push notifications may be sent (to users in earlier timezones) before the
        prompt status changes to "running". Schedule prompts in advance when sending in local time.
      </div>
      <div v-if="isEventTriggered" class="px-1">
        <div class="mb-4">
          Below is an example cURL command. Replace
          <code class="bg-goal rounded px-2 py-1 text-xs">your-user-id</code> with the recipient's
          user ID. <code class="bg-goal rounded px-2 py-1 text-xs">event_timestamp</code> is
          optional and will default to the current time.
        </div>
        <code class="bg-goal flex flex-col rounded px-3 py-2 text-xs">
          <pre>
curl -X POST https://conduit.redfastlabs.com/external_events \
  -H "Content-Type: application/json" \
  -H "Rf-App: {{ $route.params.aid }}" \
  -H "USER-ID: your-user-id" \
  -d '{
    "event_name": "rf_prompt_{{ $route.params.pid }}",
    "event_timestamp": "{{ exampleEventTimestamp }}"
  }' </pre>
        </code>
      </div>
    </div>
  </div>
</template>

<script>
import RfTimePicker from "@/components/RfTimePicker.vue";
import RfHHMMPicker from "@/components/RfHHMMPicker.vue";
import dayjs from "dayjs";
import StringUtils from "../utils/StringUtils";
import { useAppsStore } from "@/pinia/appsStore";
import TimezonesConstants from "@/utils/constants/TimezonesConstants";
import { isPushNotification } from "@/utils/prompts/promptHelpers";
import RfSwitch from "./inputs/RfSwitch.vue";
import { ref } from "vue";
import { DEFAULT_END_DATE_DAYJS } from "@/utils/DateDisplayUtils";
import RfMenu from "./menus/RfMenu.vue";

export default {
  name: "RfStartStopDatePicker",
  components: {
    RfTimePicker,
    RfHHMMPicker,
    RfSwitch,
    RfMenu,
  },
  props: {
    dateRange: Object,
    showClearIcon: Boolean,
    disabled: [Boolean, String],
    hideTime: { type: Boolean, default: false },
    model: { type: Object, default: null },
    experiments: { type: Boolean, default: false },
    disableStartDate: { type: Boolean, default: false },
  },
  setup() {
    const appsStore = useAppsStore();
    return {
      appsStore,
      dayjs,
      StringUtils,
      noEndDate: ref(false),
    };
  },
  data() {
    const actions = this.model?.actions || {};
    const data = {
      startDate: null,
      endDate: null,
      startTime: "00:00",
      endTime: "00:00",
      isEventTriggered: actions?.rf_pinpoint_campaign_event_triggered === "true",
      frequency: actions?.rf_push_frequency || "",
      localTime: actions?.rf_push_use_local_time || "false",
      quietTimeStart: actions?.rf_pinpoint_campaign_quiet_time_start,
      quietTimeEnd: actions?.rf_pinpoint_campaign_quiet_time_end,
    };
    data.showQuietTime = false;
    return data;
  },
  computed: {
    timezone() {
      const offset = this.appsStore.app?.timezone_offset || "Pacific Time (US & Canada)";
      return TimezonesConstants[offset];
    },
    actions() {
      return this.model?.actions || {};
    },
    isPushNotification() {
      return isPushNotification(this.model);
    },
    isPushSentOnce() {
      return this.isPushNotification && !this.isEventTriggered && !this.frequency;
    },
    isRunningOrEnded() {
      return this.model?.is_enabled || this.model?.is_expired;
    },
    startDateRender() {
      if (this.startDate) return dayjs(this.startDate).format("MMM Do, YYYY");
      return undefined;
    },
    endDateRender() {
      if (this.endDate) return dayjs(this.endDate).format("MMM Do, YYYY");
      return undefined;
    },
    fullStartDate() {
      let startDate = "";
      if (this.startDate instanceof Date) {
        startDate = `${this.startDate.getFullYear()}-${String(this.startDate.getMonth() + 1).padStart(2, "0")}-${String(this.startDate.getDate()).padStart(2, "0")}`;
      }
      return dayjs.utc(
        `${startDate || this.startDate} ${this.hideTime ? "00:00" : this.startTime}`,
        "YYYY-MM-DD HH:mm",
      );
    },
    fullEndDate() {
      if (this.noEndDate) return DEFAULT_END_DATE_DAYJS;
      let endDate = "";
      if (this.endDate instanceof Date) {
        endDate = `${this.endDate.getFullYear()}-${String(this.endDate.getMonth() + 1).padStart(2, "0")}-${String(this.endDate.getDate()).padStart(2, "0")}`;
      }
      return dayjs.utc(
        `${endDate || this.endDate} ${this.hideTime ? "00:00" : this.endTime}`,
        "YYYY-MM-DD HH:mm",
      );
    },
    fullDateFormat() {
      return `MMM Do, YYYY h:mma ${this.localTime === "true" ? "[local time]" : "(Z)"}`;
    },
    fullStartDateInTimezone() {
      return this.fullStartDate.tz(this.timezone, true).format(this.fullDateFormat);
    },
    cappedStartDateInTimezone() {
      return this.validSendTimes[0].tz(this.timezone, true).format(this.fullDateFormat);
    },
    cappedEndDateInTimezone() {
      return this.validSendTimes[this.validSendTimes.length - 1]
        .tz(this.timezone, true)
        .format(this.fullDateFormat);
    },
    repeatUnit() {
      return this.frequency?.split(".")?.[1];
    },
    allSendTimes() {
      const { fullEndDate, fullStartDate, frequency } = this;
      if (!fullStartDate || !fullEndDate || !frequency) {
        return [fullStartDate];
      }

      const totalTimes = Math.floor(fullEndDate.diff(fullStartDate, this.repeatUnit)) + 1;
      return Array.from({ length: totalTimes }, (_, i) => fullStartDate.add(i, this.repeatUnit));
    },
    validSendTimes() {
      const { allSendTimes, quietTimeStart, quietTimeEnd } = this;
      if (!quietTimeStart || !quietTimeEnd) {
        return allSendTimes;
      }

      const parseTime = hhmm => {
        const [h, m] = hhmm.split(":").map(Number);
        return h * 60 + m;
      };
      const [start, end] = [quietTimeStart, quietTimeEnd].map(parseTime);
      const acrossMidnight = start > end;

      return allSendTimes.filter(sendTime => {
        const timeIndex = parseTime(sendTime.format("HH:mm"));
        const isQuietTime = acrossMidnight
          ? timeIndex <= end || timeIndex >= start
          : timeIndex >= start && timeIndex <= end;
        return !isQuietTime;
      });
    },
    quietRepeatTimes() {
      return this.allSendTimes.length - this.validSendTimes.length;
    },
    repeatTimes() {
      return this.validSendTimes.length;
    },
    exampleEventTimestamp() {
      return dayjs().tz(this.timezone).format("YYYY-MM-DDTHH:mm:ssZ");
    },
  },
  watch: {
    startDate() {
      this.$emit("dateRangeUpdate", { startDate: this.fullStartDate, endDate: this.fullEndDate });
    },
    endDate() {
      this.$emit("dateRangeUpdate", { startDate: this.fullStartDate, endDate: this.fullEndDate });
    },
    startTime() {
      this.$emit("dateRangeUpdate", { startDate: this.fullStartDate, endDate: this.fullEndDate });
    },
    endTime() {
      this.$emit("dateRangeUpdate", { startDate: this.fullStartDate, endDate: this.fullEndDate });
    },
    dateRange: {
      handler() {
        const { startDate, startTime, endDate, endTime, noEndDate } = this.initDates();
        this.startDate = startDate;
        this.startTime = startTime;
        this.endDate = endDate;
        this.endTime = endTime;
        this.noEndDate = noEndDate;
      },
      deep: true,
    },
  },
  created() {
    const { startDate, startTime, endDate, endTime, noEndDate } = this.initDates();
    this.startDate = startDate;
    this.startTime = startTime;
    this.endDate = endDate;
    this.endTime = endTime;
    this.noEndDate = noEndDate;
  },
  methods: {
    handleNoEndDate(v) {
      if (!v && DEFAULT_END_DATE_DAYJS.isSame(dayjs(this.endDate), "year")) {
        this.endDate = dayjs().add(1, "year").format("YYYY-MM-DD");
      }
    },
    clearStartDate() {
      this.startDate = null;
    },
    clearEndDate() {
      this.endDate = null;
    },
    initDates() {
      const initialStartDate = dayjs.utc(this.dateRange.startDate);
      const initialEndDate = dayjs.utc(this.dateRange.endDate);

      return {
        startDate: initialStartDate.isValid() ? initialStartDate.format("YYYY-MM-DD") : null,
        startTime: initialStartDate.isValid() ? initialStartDate.format("HH:mm") : "00:00",
        endDate: initialEndDate.isValid() ? initialEndDate.format("YYYY-MM-DD") : null,
        endTime: initialEndDate.isValid() ? initialEndDate.format("HH:mm") : "00:00",
        noEndDate: initialEndDate.isSame(DEFAULT_END_DATE_DAYJS, "year"),
      };
    },
    toggleQuietTime(/*checked*/) {
      // this.showQuietTime = checked;
      // if (checked) {
      //   this.quietTimeStart = this.actions?.rf_pinpoint_campaign_quiet_time_start ?? "00:00";
      //   this.quietTimeEnd = this.actions?.rf_pinpoint_campaign_quiet_time_end ?? "00:00";
      // } else {
      //   this.quietTimeStart = undefined;
      //   this.quietTimeEnd = undefined;
      // }
    },
  },
};
</script>
